import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Menu, ChevronDown, X, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetClose } from "../ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { trackCTAClick } from "../../lib/tracking";

const PRIMARY = [
  { label: "Find Deals", to: "/top-deals", cta: "nav_top_deals" },
  { label: "Analyze", to: "/dashboard", cta: "nav_analyze" },
  { label: "Deal Score", to: "/how-scoring-works", cta: "nav_scoring" },
  { label: "Pricing", to: "/pricing", cta: "nav_pricing" },
];

export const RESOURCES = [
  { label: "FAQ & Guides", to: "/faq" },
  { label: "How to Analyze Rentals", to: "/how-to-analyze-rental-properties" },
  { label: "Cash Flow Calculator", to: "/cash-flow-calculator-real-estate" },
  { label: "Best Rental Markets", to: "/best-rental-markets-tampa-jacksonville-atlanta" },
  { label: "Realtor Reports", to: "/create-report" },
];

const linkCls = "text-sm font-semibold text-white/75 hover:text-white transition-colors min-h-[44px] inline-flex items-center px-1 rounded-md";

export function HomeNav({ isAuthenticated }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null);
  const onOpenChange = (o) => { setOpen(o); if (!o) setTimeout(() => toggleRef.current?.focus(), 0); };
  const go = (to, cta) => { if (cta) trackCTAClick(cta, to); setOpen(false); navigate(to); };

  return (
    <header className="sticky top-0 z-50 rd-nav-bg border-b border-white/10" data-testid="home-nav">
      <div className="rd-container flex items-center justify-between h-16 lg:h-[72px]">
        <Link to="/" className="flex items-center gap-2.5 rounded-md" aria-label="RealixData home" data-testid="nav-logo">
          <img src="/logo.png" alt="RealixData" className="h-11 w-auto" width="44" height="44" />
          <span className="text-base font-extrabold tracking-tight text-white">RealixData</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {PRIMARY.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => trackCTAClick(l.cta, l.to)} className={linkCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger className={`${linkCls} gap-1`} data-testid="nav-resources">
              Resources <ChevronDown className="w-4 h-4" aria-hidden="true" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 rounded-xl border border-rd-border bg-white text-rd-text p-1.5 shadow-xl">
              {RESOURCES.map((r) => (
                <DropdownMenuItem key={r.to} onSelect={() => go(r.to, "nav_resources")} className="rounded-lg px-3 py-2.5 text-sm font-medium cursor-pointer text-rd-text focus:bg-rd-green-soft focus:text-rd-text">
                  {r.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {isAuthenticated ? (
            <button type="button" onClick={() => go("/dashboard", "nav_dashboard")} className="rd-btn rd-btn-primary rd-btn-sm" data-testid="nav-dashboard">
              Dashboard <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          ) : (
            <>
              <span className="hidden sm:block"><button type="button" onClick={() => go("/auth")} className="rd-btn rd-btn-sm text-white/85 hover:text-white hover:bg-white/10" data-testid="nav-login">Log In</button></span>
              <button type="button" onClick={() => go("/auth", "nav_cta")} className="rd-btn rd-btn-primary rd-btn-sm" data-testid="nav-signup">Start Free</button>
            </>
          )}
          <button
            ref={toggleRef}
            type="button"
            onClick={() => setOpen(true)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg text-white hover:bg-white/10"
            aria-label="Open menu"
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
          >
            <Menu className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[86vw] max-w-sm bg-rd-navy border-l border-white/10 text-white p-0 [&>button]:hidden" data-testid="nav-mobile-menu">
          <div className="flex items-center justify-between h-16 px-5 border-b border-white/10">
            <SheetTitle className="flex items-center gap-2 text-white">
              <img src="/logo.png" alt="RealixData" className="h-9 w-auto" />
              <span className="font-extrabold">RealixData</span>
            </SheetTitle>
            <SheetClose className="w-11 h-11 inline-flex items-center justify-center rounded-lg hover:bg-white/10" aria-label="Close menu">
              <X className="w-5 h-5" aria-hidden="true" />
            </SheetClose>
          </div>
          <nav className="flex flex-col p-4" aria-label="Mobile">
            {PRIMARY.map((l) => (
              <button key={l.to} type="button" onClick={() => go(l.to, l.cta)} className="text-left text-base font-semibold text-white min-h-[48px] px-3 rounded-lg hover:bg-white/10" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</button>
            ))}
            <p className="rd-eyebrow text-white/40 px-3 mt-4 mb-1">Resources</p>
            {RESOURCES.map((r) => (
              <button key={r.to} type="button" onClick={() => go(r.to, "nav_resources")} className="text-left text-sm font-medium text-white/80 min-h-[44px] px-3 rounded-lg hover:bg-white/10">{r.label}</button>
            ))}
            <div className="mt-6 flex flex-col gap-2 px-1">
              {isAuthenticated ? (
                <button type="button" onClick={() => go("/dashboard", "nav_dashboard")} className="rd-btn rd-btn-primary w-full">Open Dashboard</button>
              ) : (
                <>
                  <button type="button" onClick={() => go("/auth", "nav_cta")} className="rd-btn rd-btn-primary w-full" data-testid="mobile-nav-signup">Start Free</button>
                  <button type="button" onClick={() => go("/auth")} className="rd-btn rd-btn-secondary-light w-full" data-testid="mobile-nav-login">Log In</button>
                </>
              )}
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
}
